import React, { Component } from "react";

import { withStyles } from "@material-ui/core/styles";

import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import HeaderRightLinks from "components/Header/HeaderRightLinks.js";
import HeaderLeftLinks from "components/Header/HeaderLeftLinks.js";

import styles from "assets/jss/material-kit-react/views/componentsSections/basicsStyle.js";
import "./PrivacyThirdPartyDisclosure.css";

const dashboardRoutes = [];

class PrivacyThirdPartyDisclosure extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  render() {
    return (
      <div className="third-party-disclosure-page">
        <Header
          color="white"
          routes={dashboardRoutes}
          rightLinks={<HeaderRightLinks />}
          leftLinks={<HeaderLeftLinks />}
          fixed
          changeColorOnScroll={{
            height: 400,
            color: "white",
          }}
        />

        <div className="third-party-disclosure-content">
          <article className="third-party-disclosure">
            <header className="page-header">
              <h1>Third-Party Services &amp; Privacy Disclosure</h1>
              <p>
                <strong>Last Updated:</strong> July 22, 2026
              </p>
            </header>

            <p>
              At <strong>Finding Faber</strong>, a service of John Patrick Publishing Company, we use a limited number
              of trusted third-party services to operate our website, provide search and location features, support
              account access, host files, and improve the service.
            </p>
            <p>
              This page explains the third-party services we use, why they are used, what information may be processed,
              and how cookies or similar technologies may apply.
            </p>

            <hr />

            <section>
              <h2>Third-Party Services</h2>

              <div className="service">
                <h3>Google Analytics</h3>
                <p>
                  <strong>Provider:</strong> Google LLC
                </p>
                <h4>Purpose</h4>
                <ul>
                  <li>Measure website traffic and usage</li>
                  <li>Understand how visitors interact with Finding Faber</li>
                  <li>Improve website performance and user experience</li>
                </ul>
                <h4>Information Collected</h4>
                <ul>
                  <li>Anonymous usage statistics</li>
                  <li>Device and browser information</li>
                  <li>Pages visited</li>
                  <li>Session information</li>
                  <li>IP address, processed according to Google's policies</li>
                </ul>
                <p>
                  Google Analytics may use cookies and similar technologies to collect information about how visitors
                  use our website. Optional analytics tracking is controlled by your cookie preference.
                </p>
                <p>
                  For more information:
                  <br />
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                    https://policies.google.com/privacy
                  </a>
                </p>
              </div>

              <div className="service">
                <h3>Google Maps Platform and Geocoding</h3>
                <p>
                  <strong>Provider:</strong> Google LLC
                </p>
                <p>
                  <strong>Purpose</strong>
                  <br />
                  Convert entered addresses or browser-provided coordinates into locations and ZIP codes used for local
                  search results.
                </p>
                <p>
                  Location information is used to show relevant merchants and promotions near the selected area. Browser
                  location is used only when you grant permission.
                </p>
                <p>
                  Google Privacy Policy:
                  <br />
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                    https://policies.google.com/privacy
                  </a>
                </p>
              </div>

              <div className="service">
                <h3>Google Cloud</h3>
                <p>
                  <strong>Provider:</strong> Google LLC
                </p>
                <p>
                  <strong>Purpose</strong>
                  <br />
                  We use Google Cloud services to support our application's essential infrastructure.
                </p>
                <p>
                  More information:
                  <br />
                  <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
                    https://firebase.google.com/support/privacy
                  </a>
                </p>
              </div>
            </section>

            <hr />

            <section>
              <h2>Cookies and Local Storage</h2>
              <p>Finding Faber uses cookies, local storage, and similar technologies to:</p>
              <ul>
                <li>Keep administrators signed in</li>
                <li>Maintain account sessions</li>
                <li>Remember your cookie preferences</li>
                <li>Support location-based search features</li>
                <li>Improve website performance</li>
                <li>Enhance security and essential website functionality</li>
              </ul>
              <p>Some third-party providers listed above may also set cookies necessary for their services to function.</p>
              <p>
                You can control cookies through your browser settings, although disabling certain cookies may affect
                website functionality.
              </p>
            </section>

            <hr />

            <section>
              <h2>Information Sharing</h2>
              <p>We do not sell your personal information.</p>
              <p>We share information with third-party providers only as necessary to:</p>
              <ul>
                <li>Provide requested services</li>
                <li>Secure account access</li>
                <li>Deliver location-based search results</li>
                <li>Host business assets and application data</li>
                <li>Analyze website performance when optional analytics are allowed</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p>Each provider processes information in accordance with its own privacy policy and applicable laws.</p>
            </section>

            <hr />

            <section>
              <h2>Your Choices</h2>
              <p>
                Depending on your location and applicable law, you may have the right to access, correct, delete,
                restrict, object to processing, withdraw consent, or request a copy of your data.
              </p>
              <p>
                To exercise these rights or ask questions about how your information is used, please contact John
                Patrick Publishing Company using the contact options provided on this website.
              </p>
            </section>

            <hr />

            <section>
              <h2>Cookie Categories</h2>
              <p>
                We use a combination of essential and optional cookies and similar technologies. Essential cookies are
                required for the website to function. Optional cookies are used only where applicable and, where
                required, based on your consent choices.
              </p>

              <div className="category-table-wrap">
                <table className="category-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Required?</th>
                      <th>Services</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Essential</td>
                      <td>Yes</td>
                      <td>Login session, Firebase, Google Cloud, security and account functions</td>
                    </tr>
                    <tr>
                      <td>Analytics</td>
                      <td>Optional</td>
                      <td>Google Analytics</td>
                    </tr>
                    <tr>
                      <td>Functional</td>
                      <td>Optional</td>
                      <td>Google Maps Platform, geocoding, browser location lookup</td>
                    </tr>
                    <tr>
                      <td>Interface Assets</td>
                      <td>Yes</td>
                      <td>Font Awesome CDN</td>
                    </tr>
                    <tr>
                      <td>Social Sharing</td>
                      <td>Optional</td>
                      <td>Facebook, Twitter/X, email sharing tools</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <hr />

            <section>
              <h2>Updates to This Disclosure</h2>
              <p>
                We may update this Third-Party Services &amp; Privacy Disclosure as our services evolve or as we add or
                remove service providers. The "Last Updated" date will reflect the most recent revision.
              </p>
            </section>
          </article>
        </div>

        <Footer />
      </div>
    );
  }
}

export default withStyles(styles, { withTheme: true })(PrivacyThirdPartyDisclosure);
